import { useQuery } from '@tanstack/react-query';
import { isEmpty, isNil, toString } from 'lodash';
import {
  FUND_ALLOCATIONS_BY_MD_DEFAULT_ERROR_MESSAGE,
  FUND_ALLOCATIONS_BY_MD_QUERY_KEY,
  SERVICES_FUNDS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/funds';
import { FundService } from 'services';
import { throwError } from 'utilities';
import {
  GetFundAllocationsParams,
  GetFundAllocationsResponse,
  GetFundAllocationsReturn,
  UseGetFundAllocationsParams,
} from './useGetFundAllocations.types';

const getFundAllocations = async (
  params: GetFundAllocationsParams
): Promise<GetFundAllocationsResponse | null> => {
  const { fundId, measurementDateSlug, measurementDate } = params;

  if (isNil(fundId)) {
    return null;
  }

  const fundService = new FundService();

  try {
    // Pass null when slug is empty/undefined (for "Most Recent") to omit date parameter
    // Otherwise, use the date property (YYYY-MM-DD format) instead of slug
    const dateParam = isEmpty(measurementDateSlug) ? null : (measurementDate?.date ?? null);
    const fundAllocations = (await fundService.getFundAllocations(
      toString(fundId),
      dateParam
    )) as GetFundAllocationsResponse;

    if (!fundAllocations) {
      return throwError(FUND_ALLOCATIONS_BY_MD_DEFAULT_ERROR_MESSAGE);
    }

    return fundAllocations;
  } catch (error) {
    return throwError(FUND_ALLOCATIONS_BY_MD_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetFundAllocations = (params: UseGetFundAllocationsParams): GetFundAllocationsReturn => {
  const { fund, measurementDate, shouldQueryAutomatically = true } = params;

  const fundId = fund?.id;
  // Use slug to check if "Most Recent" (empty slug), but pass date property to endpoint
  const measurementDateSlug = measurementDate?.slug;

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<
    GetFundAllocationsReturn['data'],
    Error
  >({
    // Enable query when fundId exists and measurementDate is provided (slug can be empty for "Most Recent")
    enabled: !isNil(fundId) && measurementDate !== undefined && shouldQueryAutomatically,
    queryFn: () => getFundAllocations({ fundId, measurementDateSlug, measurementDate }),
    queryKey: [FUND_ALLOCATIONS_BY_MD_QUERY_KEY, fundId, measurementDateSlug ?? 'most-recent', measurementDate?.date],
    refetchOnWindowFocus: SERVICES_FUNDS_REFETCH_ON_WINDOW_FOCUS,
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetFundAllocations;
export { getFundAllocations };

